import {useDispatch} from 'react-redux'
import {TableList} from '../../Components/TableList/TableList'
import {type Company, type CompanyAccidentCount} from '../../Store/DataSlice'
import {setDestinationView} from '../../Store/InterfaceSlice'
import {getKboUrl, toBelgianDate} from '../../Utils/Formatting'
import {isBelgianVat} from '../../Utils/Validations'

interface CompaniesTableProps {
    companies: Company[]
    isLoading: boolean
    showAdminColumns?: boolean
}

export function CompaniesTable({
                                   companies,
                                   isLoading,
                                   showAdminColumns,
                               }: CompaniesTableProps) {
    const dispatch = useDispatch()

    return (
        <TableList
            tableType="companies"
            items={companies}
            configurations={{
                vatNumber: {
                    label: 'Ondernemingsnummer',
                    show: true,
                },
                name: {
                    label: 'Naam',
                    show: true,
                },
                legalForm: {
                    label: 'Rechtsvorm',
                    show: true,
                },
                email: {
                    label: 'Email',
                    show: false,
                },
                website: {
                    label: 'Website',
                    show: false,
                },
                addressLine1: {
                    label: 'Straat en huisnummer',
                    show: false,
                },
                postalCode: {
                    label: 'Postcode',
                    show: false,
                },
                city: {
                    label: 'Gemeente',
                    show: false,
                },
                countryCode: {
                    label: 'Land',
                    show: false,
                },
                naceCode: {
                    label: 'NACE Code',
                    show: false,
                },
                branches: {
                    label: 'Vestigingen',
                    show: false,
                    getValue: (value) => (value as string[])?.join(', '),
                },
                accidentCounts: {
                    label: 'Ongevallen cijfers',
                    show: false,
                    getValue: (value) => {

                        const accidentCount = (value ?? []) as CompanyAccidentCount[];

                        if (accidentCount.length === 0) {
                            return '';
                        }

                        const lastAccidentCount = [...accidentCount].sort(
                            (a, b) => a.year - b.year
                        )[0];

                        return `${lastAccidentCount.year}: ${lastAccidentCount.count}`
                    },
                    getCsvExportValue: (value) => {
                        const accidentCount = [...(value as CompanyAccidentCount[])]
                        return accidentCount
                            .sort((ac) => ac.year)
                            .map((ac) => `${ac.year}(${ac.count})`)
                            .join(', ')
                    },
                    getExcelExportValue: (value) => {
                        const accidentCount = [...(value as CompanyAccidentCount[])]
                        return accidentCount
                            .sort((ac) => ac.year)
                            .map((ac) => `${ac.year}(${ac.count})`)
                            .join(', ')
                    },
                },
                lastKpbCheck: {
                    label: 'Laatste Kbp check',
                    show: false,
                    getCsvExportValue: (value) =>
                        value != null ? new Date(value as number).toISOString() : '',
                    getExcelExportValue: (value) =>
                        value != null ? new Date(value as number) : undefined,
                },
                lastKpbUpdate: {
                    label: 'Laatste Kbp update',
                    show: showAdminColumns ?? false,
                    getElementValue: (_value, key) => {
                        const company = companies.find((c) => c.vatNumber === key)
                        if (company == null || !isBelgianVat(company.vatNumber))
                            return <>nvt</>
                        const lastKpbUpdate = toBelgianDate(company.lastKpbUpdate)
                        if (company.lastKpbCheckSuccess === true)
                            return <>{`Success ${lastKpbUpdate}`}</>

                        return (
                            <>
                                {company.lastKpbUpdate != null
                                    ? `Failed since ${lastKpbUpdate}`
                                    : 'Failed always'}{' '}
                                <span
                                    onClick={(e) => {
                                        e.stopPropagation()
                                    }}
                                >
                  <a
                      href={getKboUrl(company.vatNumber)}
                      target="_blank"
                      rel="noreferrer"
                  >
                    See KBO
                  </a>
                </span>
                            </>
                        )
                    },
                    getCsvExportValue: (value) =>
                        value != null ? new Date(value as number).toISOString() : '',
                    getExcelExportValue: (value) =>
                        value != null ? new Date(value as number) : undefined,
                },
                kboInfo: {
                    label: 'KBO info',
                    show: showAdminColumns ?? false,
                    getElementValue: (_value, key) => {
                        const company = companies.find((c) => c.vatNumber === key)
                        if (company == null || !isBelgianVat(company.vatNumber)) {
                            return <></>
                        } else {
                            return <>{company.kboInfo}</>
                        }
                    }
                },
                lastKpbCheckSuccess: {
                    label: 'Laatste Kbp check success',
                    show: false,
                    getValue: (value) =>
                        (value as boolean | undefined) === true ? 'success' : 'failure',
                },
            }}
            localizationSection={'company'}
            getUniqueKey={(item) => item.vatNumber}
            onRowClick={(item) =>
                dispatch(
                    setDestinationView({
                        destinationView: 'company-detail',
                        detailKey: item.vatNumber,
                    })
                )
            }
            isLoading={isLoading}
        />
    )
}
